'use client';

import React, { Fragment, useEffect, useState, useCallback } from 'react';
import { useQuery } from "@apollo/client";
import { useParams, useLocation, useNavigate } from 'react-router-dom';

import Header from '../../components/Header';
import Load from '../../components/Load';
import ProfileCardLeague from '../../components/ProfileCardLeague';
import LeagueTabs, { LeagueTabId } from '../../components/LeagueTabs';
import {
  LIGA_POR_ID,
  LigaPorIdData,
  LigaPorIdVars,
  JOGADORES_DA_LIGA,
  JogadoresDaLigaData,
  JogadoresDaLigaVars,
  SEGUIDORES_DA_LIGA,
  SeguidoresDaLigaData,
  SeguidoresDaLigaVars,
} from '../../services/ligaService';
import {
  Post,
  listarPostsPorLiga,
} from '../../services/postService';
import {
  TORNEIOS_FILTRADOS,
  TorneiosFiltradosData,
  TorneiosFiltradosVars,
} from '../../services/torneioService';
import { useAuth } from '../../context/AuthContext';
import { useProfile } from '../../context/ProfileContext';
import {
  organizadoresPorLiga,
  Organizador,
} from '../../services/organizadorService';
import { useSeguirLiga } from '../../hooks/useSeguirLiga';
import { StatusTorneio } from '../../utils/StatusTorneio';
import { gql } from '@apollo/client';

const LOCAIS_ALUGUEL_DA_LIGA = gql`
  query LocaisAluguelDaLiga($ligaId: ID!) {
    locaisAluguelDaLiga(ligaId: $ligaId) {
      id
      nome
    }
  }
`;

const LeagueProfilePage: React.FC = () => {
  const { id: ligaId } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const { currentProfile } = useProfile();
  const userObj =
    (auth?.user as any) ||
    (typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('user') || 'null') : null);
  const userId = (userObj as { id?: string })?.id;

  // Identificar tipo de usuário
  const isAtleta = currentProfile?.tipo === 'ATLETA';
  const isLiga = currentProfile?.tipo === 'LIGA';
  const atletaId = isAtleta ? (userObj?.atleta?.id ?? '') : '';
  const isAdmin = userObj?.role === 'ADMIN';
  const isOwnLeague = isLiga && currentProfile?.referenciaId === ligaId;
  const seguidorUserId = userObj?.atleta?.id ?? '';

  // Aba ativa: sincronizar com hash #torneios, #jogadores, #seguidores
  const hashToTab = useCallback((hash: string): LeagueTabId => {
    if (hash === '#torneios') return 'torneios';
    if (hash === '#jogadores') return 'jogadores';
    if (hash === '#seguidores') return 'seguidores';
    return 'principal';
  }, []);
  const [activeTab, setActiveTabState] = useState<LeagueTabId>(() =>
    hashToTab(location.hash)
  );
  useEffect(() => {
    setActiveTabState(hashToTab(location.hash));
  }, [location.hash, hashToTab]);
  const onTabChange = useCallback((tab: LeagueTabId) => {
    setActiveTabState(tab);
    if (typeof window !== 'undefined') {
      if (tab === 'principal') {
        window.history.replaceState(null, '', location.pathname);
      } else {
        window.history.replaceState(null, '', `${location.pathname}#${tab}`);
      }
    }
  }, [location.pathname]);
  const onVerTorneios = useCallback(() => onTabChange('torneios'), [onTabChange]);
  const onVerJogadores = useCallback(() => onTabChange('jogadores'), [onTabChange]);

  // Filtro de status para torneios
  const [torneiosStatusFilter, setTorneiosStatusFilter] = useState<StatusTorneio | null>(null);

  // Buscar torneios da liga
  const { data: torneiosData, loading: torneiosLoading, error: torneiosError } = useQuery<
    TorneiosFiltradosData,
    TorneiosFiltradosVars
  >(TORNEIOS_FILTRADOS, {
    variables: {
      ligaId: ligaId || undefined,
      status: torneiosStatusFilter,
      page: 0,
      size: 100,
    },
    skip: !ligaId,
    fetchPolicy: 'cache-and-network',
  });
  const torneios = torneiosData?.torneiosFiltrados?.items ?? [];
  const torneiosErrorMsg = torneiosError?.message ?? null;

  // Jogadores e seguidores da liga
  const { data: jogadoresData, loading: jogadoresLoading, error: jogadoresError } = useQuery<
    JogadoresDaLigaData,
    JogadoresDaLigaVars
  >(JOGADORES_DA_LIGA, {
    variables: { ligaId: ligaId || '' },
    skip: !ligaId,
    fetchPolicy: 'cache-and-network',
  });
  const { data: seguidoresData, loading: seguidoresLoading, error: seguidoresError } = useQuery<
    SeguidoresDaLigaData,
    SeguidoresDaLigaVars
  >(SEGUIDORES_DA_LIGA, {
    variables: { ligaId: ligaId || '' },
    skip: !ligaId,
    fetchPolicy: 'cache-and-network',
  });
  const jogadores = jogadoresData?.jogadoresDaLiga ?? [];
  const seguidores = seguidoresData?.seguidoresDaLiga ?? [];
  const jogadoresErrorMsg = jogadoresError?.message ?? null;
  const seguidoresErrorMsg = seguidoresError?.message ?? null;

  const { data, loading, error } = useQuery<LigaPorIdData, LigaPorIdVars>(
    LIGA_POR_ID,
    {
      variables: { id: ligaId || '' },
      skip: !ligaId,
    },
  );
  const liga = data?.ligaPorId;

  // Buscar locais com aluguel vinculados à liga
  const { data: locaisData } = useQuery(LOCAIS_ALUGUEL_DA_LIGA, {
    variables: { ligaId: ligaId || '' },
    skip: !ligaId,
    fetchPolicy: 'cache-first',
  });
  const locaisAluguel: any[] = locaisData?.locaisAluguelDaLiga ?? [];

  // Buscar organizadores
  const [organizadores, setOrganizadores] = useState<Organizador[]>([]);
  const [organizadoresLoading, setOrganizadoresLoading] = useState(false);

  useEffect(() => {
    const fetchOrganizadores = async () => {
      if (!ligaId) return;
      setOrganizadoresLoading(true);
      try {
        const orgs = await organizadoresPorLiga(ligaId);
        setOrganizadores(orgs || []);
      } catch (err) {
        console.error('Erro ao carregar organizadores:', err);
        // Não definir erro de estado aqui para não quebrar a UI, apenas logar
      } finally {
        setOrganizadoresLoading(false);
      }
    };
    fetchOrganizadores();
  }, [ligaId]);

  // Lógica de seguir/deixar de seguir usando hook compartilhado
  const { followId, loading: loadingSeguir, error: erroSeguir, handleSeguir } = useSeguirLiga({
    seguidorUserId,
    ligaId: ligaId || '',
    skip: !seguidorUserId || !ligaId || isAdmin || isOwnLeague,
  });

  const [posts, setPosts] = useState<Post[]>([]);
  const [page, setPage] = useState(0);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsError, setPostsError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 10;

  const loadPosts = useCallback(async (pageToLoad: number, append = false) => {
    if (!ligaId) return;
    setPostsLoading(true);
    setPostsError(null);
    try {
      // Passar atletaId apenas se usuário está logado como atleta
      const curtidorAtletaId = isAtleta ? atletaId : undefined;
      const curtidorTorneioId = undefined; // Liga não pode curtir como torneio
      const curtidorLigaId = isOwnLeague ? currentProfile?.referenciaId : undefined;

      const result = await listarPostsPorLiga(
        ligaId,
        pageToLoad,
        pageSize,
        curtidorAtletaId,
        curtidorTorneioId,
        curtidorLigaId
      );
      const items = result.items || [];
      setPosts((prev) => (append ? [...prev, ...items] : items));
      setHasMore(pageToLoad < (result.totalPages - 1));
    } catch (err) {
      console.error('Erro ao carregar posts:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar posts.';
      setPostsError(errorMessage);
    } finally {
      setPostsLoading(false);
    }
  }, [ligaId, atletaId, isAtleta, pageSize]);

  useEffect(() => {
    setPage(0);
    loadPosts(0);
  }, [ligaId, loadPosts]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadPosts(nextPage, true);
  };

  // Formatar data de criação da liga
  const formatarDataCriacao = (dataString: string) => {
    const data = new Date(dataString);
    return data.toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Formatar telefone para WhatsApp (remover caracteres não numéricos)
  const formatarTelefoneWhatsApp = (telefone: string | null | undefined): string => {
    if (!telefone) return '';
    return telefone.replace(/\D/g, '');
  };

  // Obter organizador principal (primeiro da lista)
  const organizadorPrincipal = organizadores.length > 0 ? organizadores[0] : null;

  // Determinar props para Postview baseado no tipo de usuário
  const getPostviewProps = (post: Post) => {
    let torneioIdProp: string | undefined = undefined;
    let readOnlyProp = false;

    let ligaIdProp: string | undefined = undefined;

    if (isAtleta) {
      // Atleta pode curtir/comentar - Postview obtém atletaId do contexto automaticamente
      readOnlyProp = false;
    } else if (isLiga && isOwnLeague) {
      // Liga pode curtir/comentar na própria página
      readOnlyProp = false;
      ligaIdProp = currentProfile?.referenciaId;
    } else {
      // Usuário não logado ou Liga de outra página
      readOnlyProp = true;
    }

    return {
      torneioId: torneioIdProp,
      readOnly: readOnlyProp,
      ligaId: ligaIdProp,
    };
  };

  if (!ligaId) {
    return (
      <Fragment>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="pe-0">
              <div className="row">
                <div className="col-xl-8 col-xxl-9 col-lg-8">
                  <div className="alert alert-danger" role="alert">
                    Liga não encontrada.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Header />

      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left pe-0" style={{ width: '100%', maxWidth: '100%' }}>
            <div className="row justify-content-center">
              <div className="col-xl-8 col-xxl-9 col-lg-8">
                {loading && <Load />}
                {error && (
                  <div className="alert alert-danger" role="alert">
                    Erro ao carregar liga.
                  </div>
                )}
                {liga && (
                  <ProfileCardLeague
                    liga={liga}
                    follow={{
                      followId,
                      loadingSeguir,
                      erroSeguir,
                      onSeguir: handleSeguir,
                      showFollowButton: !isAdmin && !isOwnLeague && !!seguidorUserId,
                    }}
                    onTorneiosClick={onVerTorneios}
                    onJogadoresClick={onVerJogadores}
                    locaisAluguel={locaisAluguel}
                    onReservarClick={locaisAluguel.length > 0 ? () => navigate(
                      locaisAluguel.length === 1 ? `/quadras/local/${locaisAluguel[0].id}` : '/quadras'
                    ) : undefined}
                  />
                )}

                {/* Seção de Abas */}
                {liga && (
                  <LeagueTabs
                    liga={liga}
                    organizadores={organizadores}
                    organizadoresLoading={organizadoresLoading}
                    organizadorPrincipal={organizadorPrincipal}
                    isAdmin={isAdmin}
                    isOwnLeague={isOwnLeague}
                    seguidorUserId={seguidorUserId}
                    followId={followId}
                    loadingSeguir={loadingSeguir}
                    erroSeguir={erroSeguir}
                    onSeguir={handleSeguir}
                    formatarDataCriacao={formatarDataCriacao}
                    formatarTelefoneWhatsApp={formatarTelefoneWhatsApp}
                    posts={posts}
                    postsLoading={postsLoading}
                    postsError={postsError}
                    page={page}
                    hasMore={hasMore}
                    onLoadMore={handleLoadMore}
                    onPostCreated={() => loadPosts(0)}
                    getPostviewProps={getPostviewProps}
                    perfilAtivo={currentProfile}
                    activeTab={activeTab}
                    onTabChange={onTabChange}
                    onVerTorneios={onVerTorneios}
                    torneios={torneios}
                    torneiosLoading={torneiosLoading}
                    torneiosError={torneiosErrorMsg}
                    torneiosStatusFilter={torneiosStatusFilter}
                    onTorneiosStatusFilterChange={setTorneiosStatusFilter}
                    jogadores={jogadores}
                    jogadoresLoading={jogadoresLoading}
                    jogadoresError={jogadoresErrorMsg}
                    seguidores={seguidores}
                    seguidoresLoading={seguidoresLoading}
                    seguidoresError={seguidoresErrorMsg}
                    isAtleta={isAtleta}
                    isLiga={isLiga}
                    atletaId={atletaId}
                    userId={userId}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default LeagueProfilePage;
